import { Component, OnInit, ViewChild } from '@angular/core';
import { ReservationModel } from "../../models/reservations/reservation.model";
import { ReservationStatusEnum } from "../../enums/reservation-status.enum";
import { DialogService } from "primeng/dynamicdialog";
import { ReservationDetailsComponent } from "./reservation-details/reservation-details.component";
import { TranslateService } from "@ngx-translate/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ReservationService } from "../../services/reservations/reservation.service";
import { filter, forkJoin, map, Subject, takeUntil } from "rxjs";
import { RegistrationPaginationHelperModel } from "../../models/reservations/registration-pagination-helper.model";
import { MessageService } from "primeng/api";
import { VenueModel } from "../../models/venues/venue.model";
import { VenueService } from "../../services/venues/venue.service";
import { ExportReservationsFormComponent } from "./export-reservations-form/export-reservations-form.component";
import { CustomPeriodModel } from "../../models/reservations/custom-period.model";
import * as moment from "moment/moment";

@Component({
  selector: 'app-reservations',
  templateUrl: './reservations.component.html',
  styleUrls: ['./reservations.component.scss']
})
export class ReservationsComponent implements OnInit {

  //#region Fields
  public unsubscribe: Subject<void> = new Subject<void>();
  public paginationModel : RegistrationPaginationHelperModel = new RegistrationPaginationHelperModel();
  public totalPages: number;
  public totalRecords: number;
  public reservationList: Array<ReservationModel>;
  public venueId: string;
  public activeIndex: number = 0;
  public spinner: boolean = false;
  public venueName: string;
  public dateRange: Date[] = [new Date()];
  public fromTime: Date = moment(new Date()).startOf('day').toDate();
  public toTime: Date = moment(new Date()).startOf('day').toDate();
  public listOfRefusedDates: Array<CustomPeriodModel> = [];
  @ViewChild('rangeCalendar') dateRangePickerControl: any;
  public isTouch: boolean;
  public datesSpinner: boolean = false;
  public reorderDisabled: boolean = false;
  public currentReorderingIndex: number;
  public selectedReservation: string[] = [];
  //#endregion

  //#region Constructor
  constructor(
    public reservationService : ReservationService,
    public venueService: VenueService,
    public dialogService: DialogService,
    public route: ActivatedRoute,
    private router: Router,
    public translate: TranslateService,
    public messageService: MessageService
  ) {
    this.isTouch = window.innerWidth < 480 || window.innerHeight < 835;
    window.addEventListener('resize', this.handleResize.bind(this));
    this.reservationList = new Array<ReservationModel>();
  }

  ngOnInit(): void {
    this.loadTabIndex();
    this.route.params.subscribe(async params => {
      this.venueId = params['id'];
      this.paginationModel.venueId = this.venueId;
      this.getReservationsByStatus({index: this.activeIndex});
      this.getVenueDetails();
    });
  }
  //#endregion

  //#region Public Methods
  getReservationsByVenue(): void {
    this.spinner = true;
    if (this.paginationModel.pageNumber === 1) {
      this.reservationList = new Array<ReservationModel>();
    }
    this.reservationService.getReservationsByVenue(this.paginationModel)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: any) => {
          this.handleGetReservationsResponse(response);
        },
        error: errorResponse => {
          this.spinner = false;
        }
      });
  };

  getPriorityReservationsByVenue(): void {
    this.spinner = true;
    if (this.paginationModel.pageNumber === 1) {
      this.reservationList = new Array<ReservationModel>();
    }
    this.reservationService.getPriorityReservationsByVenue(this.paginationModel)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: any) => {
          this.handleGetReservationsResponse(response);
        },
        error: errorResponse => {
          this.spinner = false;
        }
      });
  }

  getPastReservationsByVenue(): void {
    this.spinner = true;
    if (this.paginationModel.pageNumber === 1) {
      this.reservationList = new Array<ReservationModel>();
    }
    this.reservationService.getPastReservationsByVenue(this.paginationModel)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: any) => {
          this.handleGetReservationsResponse(response);
        },
        error: errorResponse => {
          this.spinner = false;
        }
      });
  };

  public handleResize(): void {
    this.dateRangePickerControl.toggle();
    this.isTouch = window.innerWidth < 480 || window.innerHeight < 835;
    this.dateRangePickerControl.touchUI = this.isTouch;
  }

  openDetailsPopup(res: ReservationModel): void {
    const ref = this.dialogService.open(ReservationDetailsComponent, {
      data: {
        id: res.id,
        venueId: this.venueId,
        pastList: this.activeIndex === 4,
        priorityList: this.activeIndex === 0
      },
      header: this.translate.instant('RESERVATION.RESERVATION_DETAILS'),
      width: '385px'
    });
    ref.onClose.subscribe((item: any) => {
      if (item !== null && item !== undefined) {
        let summary: string;
        if (item.priorityChanged) {
          summary = this.translate.instant("RESERVATION.RESERVATION_ADDED_TO_PRIORITY_LIST");
        } else {
          summary = item ? this.translate.instant("RESERVATION.RESERVATION_APPROVED") :
            this.translate.instant("RESERVATION.RESERVATION_DECLINED");
        }
        this.messageService.add({severity: 'success', summary, life: 3000});
        this.reservationList = new Array<ReservationModel>();
        this.paginationModel.pageNumber = 1;
        this.getReservationsByStatus({index: this.activeIndex});
      }
    });
  }

  loadMoreReservations(): void {
    this.paginationModel.pageNumber = this.paginationModel.pageNumber + 1;
    this.getReservationsByStatus({index: this.activeIndex}, false);
  }

  getReservationsByStatus(event: any, refresh: boolean = true): void {
    this.updateTabIndexParam(event.index);
    let status;

    if (event.index === 1) {
      status = ReservationStatusEnum.Pending;
    } else if (event.index === 2) {
      status = ReservationStatusEnum.Approved;
    } else if (event.index === 3) {
      status = ReservationStatusEnum.Declined;
    } else if (event.index === 5) {
      status = ReservationStatusEnum.Canceled;
    }

    if (refresh) {
      this.paginationModel.pageNumber = 1;
    }

    if (status) {
      this.paginationModel.status = status;
      this.getReservationsByVenue();
    }

    if (event.index === 0) {
      this.getPriorityReservationsByVenue();
    } else if (event.index === 4) {
      this.getPastReservationsByVenue();
    }
  }

  public getVenueDetails(refresh: boolean = false): void {
    this.venueService.getvenuedetails(this.venueId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: VenueModel) => {
          if (!refresh) {
            this.venueName = response.name;
          }
          this.listOfRefusedDates = response.venueNotAlowReservationPeriods;
          this.datesSpinner = false;
        }
      });
  }

  public exportExcelToEmail(): void {
    this.dialogService.open(ExportReservationsFormComponent, {
      data: {
        venueId: this.venueId,
        venueName: this.venueName
      },
      header: this.translate.instant('RESERVATION.EXPORT'),
      width: '335px'
    });
  }

  public isRangeDate(date: any, index: number, both: boolean = false): boolean {
    const selected = this.dateRangePickerControl.value && this.dateRangePickerControl.value[index] ? this.dateRangePickerControl.value[index] : null;
    if (selected) {
      const equals = date.day === selected.getDate() && date.year === selected.getFullYear() && date.month === selected.getMonth();

      if (both && equals) {
        return !this.dateRangePickerControl.value[1];
      }

      return equals;
    }
    return false;
  }

  public addRefuseReservationsRange(): void {
    const model = new CustomPeriodModel();
    model.venueId = this.venueId;
    model.startDateTime = moment(this.dateRange[0]).format('yyyy-MM-DDTHH:mm:ss.SSS') + 'Z';
    model.endDateTime = moment(this.dateRange[1]).format('yyyy-MM-DDTHH:mm:ss.SSS') + 'Z';
    model.rangeStartTime = moment(this.fromTime).format('HH:mm');
    model.rangeEndTime = moment(this.toTime).format('HH:mm');

    this.datesSpinner = true;
    this.reservationService.addNotAllowedPeriod(model)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: any) => {
          this.resetDates();
          this.getVenueDetails(true);
        },
        error: errorResponse => {
          this.datesSpinner = false;
        }
      });
  }

  public resetDates(): void {
    this.dateRange = [new Date()];
    this.toTime = moment(new Date()).startOf('day').toDate();
    this.fromTime = moment(new Date()).startOf('day').toDate();
  }

  public showTime(dateTime: CustomPeriodModel) {
    return dateTime.rangeStartTime && dateTime.rangeEndTime
      && dateTime.rangeStartTime.trim() !== '' && dateTime.endDateTime.trim() !== '';
  }

  public parseTime(rangeTime: string): Date {
    const [hours, minutes] = rangeTime.split(':');
    const dateObj = new Date();
    dateObj.setHours(parseInt(hours), parseInt(minutes));
    return dateObj;
  }

  public deleteNotAllowedPeriod(id: string): void {
    this.datesSpinner = true;
    this.reservationService.deleteNotAllowedPeriod({id})
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: any) => {
          this.getVenueDetails(true);
        },
        error: errorResponse => {
          this.datesSpinner = false;
        }
      });
  }

  public onReorder(event: any): void {
    this.reorderDisabled = true;
    this.currentReorderingIndex = this.reservationList.findIndex(x => x.id === event[0].id);
    let newPriority;
    if (this.currentReorderingIndex !== 0) {
      newPriority = this.reservationList[this.currentReorderingIndex - 1].priorityNumber;
    } else {
      newPriority = 0;
    }
    this.reservationService.reorderPriorityList(event[0].id, newPriority)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: any) => {
          let requests = [];
          const page = this.paginationModel.pageNumber;
          for (let i = 1; i <= page; i++) {
            this.paginationModel.pageNumber = i;
            requests.push(this.reservationService.getPriorityReservationsByVenue(this.paginationModel));
          }

          forkJoin(requests).pipe(
            map((results: any) => results.map((result: any) => result.data)),
            map((results: any) => [].concat(...results))
          ).subscribe({
            next: (concatenatedList: ReservationModel[]) => {
              this.reservationList = concatenatedList;
              this.reorderDisabled = false;
              this.selectedReservation = [];
            }
           });
          },
        error: errorResponse => {
          this.reorderDisabled = false;
        }
      });
  }

  public handleGetReservationsResponse(response: any): void {
    this.totalPages = response.totalPages;
    this.totalRecords = response.totalRecords;
    if (this.paginationModel.pageNumber > 1) {
      this.reservationList = this.reservationList.concat(response.data);
    }
    else {
      this.reservationList = response.data;
    }
    this.spinner = false;
  }

  public loadTabIndex(): void {
    this.route.queryParams
      .pipe(
        filter(params => params.activeIndex)
      ).subscribe(params => {
      this.activeIndex = +params.activeIndex;
    });
  }

  public updateTabIndexParam(activeIndex: number): void {
    this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams: {activeIndex: activeIndex},
        queryParamsHandling: 'merge',
        replaceUrl: true
      })
  }
  //#endregion
  //#Private Methods
  //#endregion
}
